import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

export interface AntibodyInteractivityProps {
  hoveredRef: number | null;
  proteinIdsByRef: Record<number, string>;
  inaccessibleProteinIdsByRef: Record<number, string>;
  selectedRef: number | null;
  setHoveredRef: (newHoveredRef: number | null) => void;
  setSelectedRef: (newSelectedRefs: number | null) => void;
}

type InteractiveElement = 'g' | 'div';

export const INTERACTIVE_WRAPPER_DATA_ATTR = 'data-antibody-interactive-component-wrapper';

export interface InteractiveComponentWrapperProps {
  children: ReactNode;
  componentRef: number;
  interactivity?: AntibodyInteractivityProps;
  interactiveElement?: InteractiveElement;
}

export default function InteractiveComponentWrapper({
  children,
  componentRef,
  interactivity,
  interactiveElement = 'g',
}: InteractiveComponentWrapperProps): JSX.Element {
  if (interactivity == null) {
    return <>{children}</>;
  }

  const disableClick = interactivity.inaccessibleProteinIdsByRef[componentRef] != null;

  const props = {
    className: css(disableClick ? styles.disabled : styles.group),
    onClick: (): void => {
      if (disableClick) {
        return;
      }
      if (interactivity.selectedRef === componentRef) {
        interactivity.setSelectedRef(null);
      } else {
        interactivity.setSelectedRef(componentRef);
      }
    },
    onMouseOver: (): void => {
      interactivity.setHoveredRef(componentRef);
    },
    onMouseOut: (): void => {
      interactivity.setHoveredRef(null);
    },
    [INTERACTIVE_WRAPPER_DATA_ATTR]: true,
    ...getTestProps({
      component: 'InteractiveComponentWrapper',
      element: 'root',
      key: componentRef.toString(),
    }),
  };

  const InteractiveElementTag = interactiveElement as React.ElementType;
  return <InteractiveElementTag {...props}>{children}</InteractiveElementTag>;
}

const styles = StyleSheet.create({
  group: {
    cursor: 'pointer',
  },
  disabled: {
    cursor: 'not-allowed',
  },
});
